import { Component, EventEmitter, OnInit, Output, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NewContractPymesService } from '../../../services/new-contract-pymes.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TipoVia } from 'src/app/interfaces';
import { UtilService } from 'src/app/services/util.service';
import { Select } from '@interfaces';
import { AuthService } from 'src/app/auth/auth.service';
import { UtilValidatorsService } from 'src/app/commons/validations-format/util-validators.service';

@Component({
  selector: 'app-installation-data-pymes',
  templateUrl: './installation-data-pymes.component.html',
  styleUrls: ['./installation-data-pymes.component.scss']
})
export class InstallationDataPymesComponent implements OnInit, OnDestroy {
  @Output() formValid: EventEmitter<any> = new EventEmitter();
  private destroyed$: Subject<boolean> = new Subject<boolean>();
  public installationDataMainForm: FormGroup;
  public installationDataAddressForm: FormGroup;
  private viaTypesListRes = [];
  public viaTypesList: TipoVia[] = [];
  private formMainValid = false;
  private formAddressValid = false;
  private installationSavedData;
  public townList: Select[] = [];
  public defaultValueTown = '';
  public defaultValueRoadType = '';
  public useSavedValues: boolean;
  private firstTime = true;
  public addressDisabled = false;
  public townDisabled = true;
  public isUserSpain = true;

  constructor(
    private formBuilder: FormBuilder,
    private newContractPymesService: NewContractPymesService,
    private utilService: UtilService,
    private authService: AuthService,
    private utilValidatorsService: UtilValidatorsService
  ) { }

  ngOnInit(): void {
    this.isUserSpain = this.authService.userData.data.country === 'ES';
    this.getVia();
    this.createForm();
    this.subscribeForm();
    this.subscribeAddress();
    this.subscribePostalCode();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private createForm(): void {
    this.installationDataMainForm = this.formBuilder.group({
      ins_name: ['', [Validators.required, Validators.maxLength(40)]],
      ins_cups: [
        { value: this.newContractPymesService.getSavedData().value.analysis?.cups || '', disabled: true },
        [Validators.required, Validators.maxLength(22)]
      ],
      ins_access_rate: [
        { value: this.newContractPymesService.getSavedData().value.analysis?.accessFee || '', disabled: true }
        , [Validators.required]
      ],
      ins_cnae: [
        (
          this.newContractPymesService.getSavedData().value.installation?.cnae
          || this.newContractPymesService.getSavedData().value.business?.cnae
          || ''
        ),
        [Validators.required, this.utilValidatorsService.validateCNAE]
      ]
    });
    this.installationDataAddressForm = this.formBuilder.group({
      ins_road_type: [{ value: '', disabled: false }, Validators.required],
      ins_road_name: [{ value: '', disabled: false }, [Validators.required, Validators.maxLength(100)]],
      ins_number: [{ value: '', disabled: false }, [Validators.maxLength(8), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]],
      ins_gate: [{ value: '', disabled: false }, Validators.maxLength(10)],
      ins_stair: [{ value: '', disabled: false }, [Validators.maxLength(3), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]],
      ins_floor: [{ value: '', disabled: false }, [Validators.maxLength(3), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]],
      ins_door: [{ value: '', disabled: false }, [Validators.maxLength(3), Validators.pattern(this.utilValidatorsService.alphaNumericPattern)]],
      ins_postal_code: [{ value: '', disabled: false }],
      ins_postal_code_PT1: [{ value: '', disabled: false }],
      ins_postal_code_PT2: [{ value: '', disabled: false }],
      ins_town: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(100)]],
      ins_province: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(100)]]
    });

    this.initPostalCodeConfigPortugal();
  }

  private subscribeForm(): void {
    this.installationDataMainForm.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe(result => {
      this.newContractPymesService.setSavedData({
        installation: this.mapValuesToSave()
      });
      if (this.installationDataMainForm.enabled && result === 'VALID') {
        this.formMainValid = true;
        if (this.formAddressValid) {
          this.formValid.emit({ value: true, tab: 2 });
        }
      } else {
        this.formMainValid = false;
        this.formValid.emit({ value: false, tab: 2 });
      }
    });
  }

  private subscribeAddress(): void {
    this.installationDataAddressForm.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe(result => {
      this.newContractPymesService.setSavedData({
        installation: this.mapValuesToSave()
      });
      this.installationDataMainForm.updateValueAndValidity();
      if (this.installationDataAddressForm.enabled && result === 'VALID' &&
        (this.isValidPostalCode() &&
          !!this.installationDataAddressForm.controls.ins_town.value &&
          !!this.installationDataAddressForm.controls.ins_province.value)
      ) {
        this.formAddressValid = true;
        if (this.formMainValid || this.installationDataMainForm.status === 'DISABLED') {
          this.formValid.emit({ value: true, tab: 2 });
        }
      } else if (result === 'DISABLED') {
        this.formAddressValid = true;
        if (this.formMainValid) {
          this.formValid.emit({ value: true, tab: 2 });
        }
      } else {
        this.formAddressValid = false;
        this.formValid.emit({ value: false, tab: 2 });
      }
    });
  }

  private getVia(): void {
    this.newContractPymesService.getTipoVia().pipe(takeUntil(this.destroyed$)).subscribe(res => {
      if (res.success) {
        this.viaTypesList = [...this.mapResponse(res.data)];
        this.viaTypesListRes = [...this.mapResponse(res.data)];
      } else {
        this.viaTypesList = [];
        this.viaTypesListRes = [];
        this.utilService.toastErrors(res);
      }
      this.getSavedData();
    }, error => {
      this.getSavedData();
      this.utilService.toastErrors(error);
    });
  }

  private getPostalCode(response): void {
    this.newContractPymesService.getCodigoPostal(this.installationDataAddressForm.controls.ins_postal_code.value)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        if (res.success) {
          this.townList = [...this.mapTownListResponse(res.data.poblaciones)];
          this.installationDataAddressForm.controls.ins_town.enable();
          this.defaultValueTown = this.townList.find(el =>
            el.value === response.installation.codigoPoblacionDireccion
          )?.value || this.townList[0].value;
          this.townDisabled = false;
          this.installationDataAddressForm.controls.ins_province.disable();
          this.installationDataAddressForm.patchValue({
            ins_town: this.defaultValueTown,
            ins_province: res.data.nombreProvincia,
          });
        } else {
          this.utilService.toastErrors(res);
        }
      }, error => {
        this.utilService.toastErrors(error);
      });
    (!response.installation?.nombreProvinciaDireccion && !!response.installation?.codPostalDireccion) ?
      this.installationDataAddressForm.controls.ins_province.enable() :
      this.installationDataAddressForm.controls.ins_province.patchValue(response.installation?.nombreProvinciaDireccion);
  }

  private getSavedData(): void {
    this.newContractPymesService.getSavedData().pipe(takeUntil(this.destroyed$)).subscribe(res => {
      if (res) {
        if ((res.installationFromCups || res.savedDataFromDraft || res.codigoInstalacion) && this.firstTime) {
          this.firstTime = false;
          this.installationDataMainForm.patchValue({
            ins_name: res.installation?.nombre,
            ins_cups: res.analysis?.cups,
            ins_access_rate: res.analysis?.accessFee,
            ins_cnae: res.installation?.cnae || res.business?.cnae,
          });
          this.defaultValueRoadType = this.viaTypesListRes.find(el => el.label === res.installation?.nombreTipoViaDireccion)?.value;
          this.installationDataAddressForm.patchValue({
            ins_road_type: this.defaultValueRoadType,
            ins_road_name: res.installation?.nombreDireccion,
            ins_number: res.installation?.numeroDireccion,
            ins_stair: res.installation?.escaleraDireccion,
            ins_gate: res.installation?.portalDireccion,
            ins_floor: res.installation?.pisoDireccion,
            ins_door: res.installation?.puertaDireccion,
            ins_postal_code: res.installation?.codPostalDireccion,
          });
          this.savePostalCodeValuePortugal(res.installation?.codPostalDireccion);
          if (
            res.installation?.codPostalDireccion
            && this.isValidPostalCode()
          ) {
            this.getPostalCode(res);
          }
        } else if ((res.analysis?.cups !== this.installationDataMainForm.controls.ins_cups.value) && this.firstTime) {
          this.installationDataMainForm.patchValue({
            ins_cups: res.analysis?.cups,
            ins_access_rate: res.analysis?.accessFee,
            ins_cnae: res.installation?.cnae || res.business?.cnae,
          });
        }
        this.installationSavedData = res.business;
      }
    });
  }

  private subscribePostalCode(): void {
    this.installationDataAddressForm.controls.ins_postal_code.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      if (value && this.isValidPostalCode()) {
        this.newContractPymesService.getCodigoPostal(this.installationDataAddressForm.controls.ins_postal_code.value)
          .pipe(takeUntil(this.destroyed$)).subscribe(res => {
            if (res.success) {
              if (!this.useSavedValues) {
                this.townList = [...this.mapTownListResponse(res.data.poblaciones)];
                this.installationDataAddressForm.controls.ins_town.enable();
                this.townDisabled = false;
                const town = this.townList.find(
                  el => el.value === this.newContractPymesService.getSavedData().value.installation?.codigoPoblacionDireccion
                )?.value;
                this.defaultValueTown = town || this.townList[0]?.value;
                this.installationDataAddressForm.patchValue({
                  ins_town: this.defaultValueTown,
                  ins_province: res.data.nombreProvincia
                });
              } else {
                if (res.data.codPostal != null) {
                  this.townList = [...this.mapTownListResponse(res.data.poblaciones)];
                  this.installationDataAddressForm.controls.ins_province.patchValue(res.data.nombreProvincia);
                }
              }
            } else {
              this.utilService.toastErrors(res);
            }
          }, error => {
            this.utilService.toastErrors(error);
          });
      } else {
        this.resetCodigoPostal();
      }
    });
  }

  private mapResponse(values): TipoVia[] {
    const aux = [];
    values.forEach(element => {
      aux.push({ label: element.nombreTipoVia, value: element.idTipoVia });
    });
    return aux;
  }

  public onUseDir(e): void {
    if (e.checked) {
      this.useSavedValues = true;
      this.installationDataAddressForm.disable();
      this.addressDisabled = true;
      const roadType = this.viaTypesListRes.find(el => el.label === this.installationSavedData.nombreTipoViaDireccion);
      this.defaultValueRoadType = roadType?.value;
      this.savePostalCodeValuePortugal(this.installationSavedData.codPostalDireccion);
      this.installationDataAddressForm.patchValue({
        ins_road_type: roadType?.value,
        ins_road_name: this.installationSavedData.nombreDireccion,
        ins_number: this.installationSavedData.numeroDireccion,
        ins_gate: this.installationSavedData.portalDireccion,
        ins_stair: this.installationSavedData.escaleraDireccion,
        ins_floor: this.installationSavedData.pisoDireccion,
        ins_door: this.installationSavedData.puertaDireccion,
        ins_postal_code: this.installationSavedData.codPostalDireccion,
        ins_town: this.installationSavedData.nombrePoblacionDireccion,
        ins_province: this.installationSavedData.nombreProvinciaDireccion
      });
    } else {
      this.addressDisabled = false;
      this.useSavedValues = false;
      this.defaultValueRoadType = null;
      this.installationDataAddressForm.controls.ins_road_type.patchValue(null);
      this.installationDataAddressForm.reset();
      this.installationDataAddressForm.enable();
      this.townDisabled = true;
      this.installationDataAddressForm.controls.ins_town.disable();
      this.installationDataAddressForm.controls.ins_province.disable();
    }
  }

  private mapValuesToSave(): any {
    const savedData = this.newContractPymesService.getSavedData().value;
    let nombreTipoVia = null;
    let codigoPoblacionDireccion = null;
    nombreTipoVia = this.viaTypesListRes.find(el => el.value === this.installationDataAddressForm.controls.ins_road_type.value);
    nombreTipoVia = nombreTipoVia && nombreTipoVia.label || '';
    let nombrePoblacion = null;
    if (this.installationDataAddressForm.value.ins_town) {
      if (this.installationDataAddressForm.controls.ins_town.enabled) {
        if (this.townList.length > 0) {
          nombrePoblacion = this.townList.find(el => el.value === this.installationDataAddressForm.value.ins_town)?.label;
          codigoPoblacionDireccion = this.installationDataAddressForm.value.ins_town;
        } else {
          nombrePoblacion = this.installationDataAddressForm.value.ins_town;
          codigoPoblacionDireccion = this.installationDataAddressForm.value.ins_town;
        }
      } else {
        nombrePoblacion = this.installationDataAddressForm.value.ins_town;
        codigoPoblacionDireccion = this.installationSavedData.codigoPoblacionDireccion;
      }
    }
    const contactoCRM = this.newContractPymesService.getSavedData().value.contactoCRM;
    const codigoRazonSocial = this.newContractPymesService.getSavedData().value.codigoRazonSocial;
    const idBorrador = this.newContractPymesService.getSavedData().value.idBorrador;
    return {
      codigoCampana: savedData.analysis?.campaign,
      contactoCRM,
      idBorrador,
      codigoRazonSocial,
      nombre: this.installationDataMainForm.controls.ins_name.value,
      cups: this.installationDataMainForm.controls.ins_cups.value,
      tarifaSips: this.installationDataMainForm.controls.ins_access_rate.value,
      cnae: this.installationDataMainForm.controls.ins_cnae.value,
      nombreTipoViaDireccion: nombreTipoVia || this.installationDataAddressForm.controls.ins_road_type.value,
      nombreDireccion: this.installationDataAddressForm.controls.ins_road_name.value,
      numeroDireccion: this.installationDataAddressForm.controls.ins_number.value,
      portalDireccion: this.installationDataAddressForm.controls.ins_gate.value,
      escaleraDireccion: this.installationDataAddressForm.controls.ins_stair.value,
      pisoDireccion: this.installationDataAddressForm.controls.ins_floor.value,
      puertaDireccion: this.installationDataAddressForm.controls.ins_door.value,
      codPostalDireccion: this.installationDataAddressForm.controls.ins_postal_code.value,
      nombrePoblacionDireccion: nombrePoblacion || null,
      nombreProvinciaDireccion: this.installationDataAddressForm.controls.ins_province.value || null,
      codigoPoblacionDireccion: codigoPoblacionDireccion || nombrePoblacion
    };
  }
  private mapTownListResponse(values): Select[] {
    const aux = [];
    values?.forEach(element => {
      aux.push({ label: element.nombrePoblacion, value: element.codigoPoblacion });
    });
    return aux;
  }

  private resetCodigoPostal(): void {
    this.townList = [];
    this.installationDataAddressForm.controls.ins_town.setValue(null);
    this.installationDataAddressForm.controls.ins_town.disable();
    this.installationDataAddressForm.controls.ins_province.setValue(null);
  }

  private initPostalCodeConfigPortugal(): void {
    if (!this.isUserSpain) {
      this.installationDataAddressForm.get('ins_postal_code_PT1').setValidators([Validators.required, Validators.maxLength(4)]);
      this.installationDataAddressForm.get('ins_postal_code_PT2').setValidators([Validators.required, Validators.maxLength(3)]);
      this.installationDataAddressForm.get('ins_postal_code').setValidators([
        Validators.required,
        Validators.pattern(this.utilValidatorsService.postalCodePTPattern)
      ]);

      this.installationDataAddressForm.get('ins_postal_code_PT1').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
        this.installationDataAddressForm.get('ins_postal_code').setValue(
          value + '-' + this.installationDataAddressForm.get('ins_postal_code_PT2').value
        );
      });
      this.installationDataAddressForm.get('ins_postal_code_PT2').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
        this.installationDataAddressForm.get('ins_postal_code').setValue(
          this.installationDataAddressForm.get('ins_postal_code_PT1').value + '-' + value
        );
      });
    } else {
      this.installationDataAddressForm.get('ins_postal_code').setValidators([Validators.required]);
    }
  }

  private savePostalCodeValuePortugal(value: string): void {
    if (!this.isUserSpain && value) {
      this.installationDataAddressForm.get('ins_postal_code_PT1').patchValue(value?.slice(0, 4));
      this.installationDataAddressForm.get('ins_postal_code_PT2').patchValue(value?.slice(5, 8));
    }
  }

  private isValidPostalCode(): boolean {
    if (this.isUserSpain) {
      return this.installationDataAddressForm.get('ins_postal_code').value?.length > 4;
    } else {
      return this.installationDataAddressForm.get('ins_postal_code_PT1').value?.length === 4 &&
        this.installationDataAddressForm.get('ins_postal_code_PT2').value?.length === 3 &&
        this.installationDataAddressForm.get('ins_postal_code').value?.length === 8;
    }
  }
}
